import { apiRequest } from "./api";
import { getAuth } from "./auth";

/* The Cars and Drivers lists, kept while the app is open (24 Oct 2026, mock Four_1
   change 10). Switching Cars ⇄ Drivers unmounted the list and fetched it again - the
   whole list and every photo, with a spinner, on phones with little signal. Now a list
   seen in the last 45 minutes shows at once and is refreshed quietly behind it when it
   is more than a minute old; the other tab's list is fetched once the first is shown.

   Only the public lists are kept, under the signed-in number (or none), so a different
   login never sees another's copy. 45 minutes because the photo links in a list are
   good for at least an hour (backend lib/s3Helper signPhotoUrl). A copy also sits in
   sessionStorage, so a reload in the same tab starts from it; it never outlives the tab. */

const FRESH_MS = 60 * 1000;
const KEEP_MS = 45 * 60 * 1000;
const STORE = "sramika_lists_v1";
const MAX_ENTRIES = 4;

const mem = new Map();
const inflight = new Map();

const keyOf = (path) => `${getAuth()?.phone || "-"}|${path}`;

function readStore() {
  try { return JSON.parse(sessionStorage.getItem(STORE) || "{}") || {}; } catch { return {}; }
}
function writeStore(key, entry) {
  try {
    const all = readStore();
    all[key] = entry;
    const keep = Object.entries(all).sort((a, b) => b[1].at - a[1].at).slice(0, MAX_ENTRIES);
    sessionStorage.setItem(STORE, JSON.stringify(Object.fromEntries(keep)));
  } catch { /* storage full or blocked: the memory copy still works */ }
}

function entryFor(path) {
  const key = keyOf(path);
  let e = mem.get(key);
  if (!e) { e = readStore()[key]; if (e) mem.set(key, e); }
  if (!e || Date.now() - e.at > KEEP_MS) return null;
  return e;
}

/** The list's last answer, if it is still good to show; null otherwise. */
export function cachedList(path) {
  return entryFor(path)?.data || null;
}

/** Whether the kept answer is new enough not to ask again. */
export function isFresh(path) {
  const e = entryFor(path);
  return !!e && Date.now() - e.at < FRESH_MS;
}

/** Asks the server (one request at a time per list) and keeps the answer. */
export function fetchList(path) {
  const key = keyOf(path);
  if (inflight.has(key)) return inflight.get(key);
  const p = apiRequest(path)
    .then((data) => {
      const entry = { at: Date.now(), data };
      mem.set(key, entry);
      writeStore(key, entry);
      return data;
    })
    .finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
}

/** The other tab's list, fetched quietly once the phone is idle. */
export function prefetchList(path) {
  if (isFresh(path)) return;
  const go = () => { fetchList(path).catch(() => {}); };
  if (typeof window !== "undefined" && window.requestIdleCallback) window.requestIdleCallback(go, { timeout: 4000 });
  else setTimeout(go, 1500);
}

/** The exact paths the two lists ask for when no pickup is set - what a prefetch warms. */
export const carsListPath = (city) => `/cars/visible?city=${encodeURIComponent(city)}&status=APPROVED`;
export const driversListPath = (city) => `/alting-drivers?city=${encodeURIComponent(city)}`;

/* How many in a kept list carry the name searched for - for "no car by this name, N
   found in Drivers" (mock Four_1 change 8). Every word of three letters or more must be
   in the name; nothing is fetched for it. */
const plain = (s) => String(s || "").toLowerCase().replace(/[.,]+/g, " ").replace(/\s+/g, " ").trim();
export function countNamed(path, q, nameOf) {
  const words = plain(q).split(" ").filter((w) => w.length >= 3);
  if (!words.length) return 0;
  return (cachedList(path)?.data || []).filter((x) => {
    const n = plain(nameOf(x));
    return n && words.every((w) => n.includes(w));
  }).length;
}
